import React, { useEffect, useState } from 'react'
import { useAuditLogStore } from '@store'
import { Shield, Download, RefreshCw, Filter, X, Calendar, User, Building2 } from 'lucide-react'
import { DataTable, SearchBar, Select, Button, Input, PageHeader } from '@components/common'
import './AuditLogs.css'

/**
 * Format date to readable string
 */
const formatDate = (dateString) => {
	if (!dateString) return '-'
	const date = new Date(dateString)
	return date.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	})
}

/**
 * Get badge color for event category
 */
const getEventBadgeColor = (action) => {
	if (action.includes('SUCCESS') || action.includes('CREATED') || action.includes('ACTIVATED')) {
		return 'success'
	}
	if (action.includes('FAILED') || action.includes('DELETED') || action.includes('SUSPENDED')) {
		return 'danger'
	}
	if (action.includes('UPDATED') || action.includes('CHANGED')) {
		return 'warning'
	}
	return 'info'
}

/**
 * Audit Logs Page (Super Admin Only)
 *
 * Read-only audit log viewer with:
 * - Search and advanced filtering
 * - Date range filtering
 * - Export to CSV
 * - Real-time pagination
 * - Event code categorization
 *
 * Features:
 * - Filter by user, company, action, date range
 * - Search in action/description
 * - Export current view to CSV
 * - Responsive table design
 * - No edit/delete operations (read-only)
 */
const AuditLogs = () => {
	const {
		logs,
		eventCodes,
		distinctActions,
		loading,
		filters,
		pagination,
		fetchLogs,
		fetchEventCodes,
		fetchDistinctActions,
		setSearch,
		setUserFilter,
		setCompanyFilter,
		setActionFilter,
		setDateRange,
		clearFilters,
		setPage,
		nextPage,
		prevPage,
		setPerPage,
		exportLogs,
		refresh,
	} = useAuditLogStore()

	const [showFilters, setShowFilters] = useState(false)
	const [localDateFrom, setLocalDateFrom] = useState('')
	const [localDateTo, setLocalDateTo] = useState('')

	// Fetch logs and metadata on mount
	useEffect(() => {
		fetchLogs()
		fetchEventCodes()
		fetchDistinctActions()
	}, [])

	/**
	 * Handle export to CSV
	 */
	const handleExport = () => {
		try {
			exportLogs()
		} catch (error) {
			alert(error.message)
		}
	}

	/**
	 * Handle date range apply
	 */
	const handleApplyDateRange = () => {
		setDateRange(localDateFrom, localDateTo)
	}

	/**
	 * Handle clear date range
	 */
	const handleClearDateRange = () => {
		setLocalDateFrom('')
		setLocalDateTo('')
		setDateRange('', '')
	}

	/**
	 * Handle clear all filters
	 */
	const handleClearAllFilters = () => {
		setLocalDateFrom('')
		setLocalDateTo('')
		clearFilters()
	}

	/**
	 * Count active filters
	 */
	const activeFiltersCount = () => {
		let count = 0
		if (filters.user_id) count++
		if (filters.company_id) count++
		if (filters.action) count++
		if (filters.date_from || filters.date_to) count++
		if (filters.search) count++
		return count
	}

	// DataTable columns configuration
	const columns = [
		{
			key: 'id',
			label: 'ID',
			width: '60px',
			render: (row) => <span className="log-id">#{row.id}</span>,
		},
		{
			key: 'created_at',
			label: 'Date & Time',
			width: '180px',
			render: (row) => (
				<div className="log-datetime">
					<Calendar className="icon" size={14} />
					<span>{formatDate(row.created_at)}</span>
				</div>
			),
		},
		{
			key: 'action',
			label: 'Event',
			width: '200px',
			render: (row) => (
				<span className={`badge badge-${getEventBadgeColor(row.action)}`}>
					{row.action}
				</span>
			),
		},
		{
			key: 'description',
			label: 'Description',
			render: (row) => (
				<div className="log-description" title={row.description}>
					{row.description || '-'}
				</div>
			),
		},
		{
			key: 'user_id',
			label: 'User',
			width: '80px',
			render: (row) => (
				<div className="log-user">
					{row.user_id ? (
						<>
							<User className="icon" size={14} />
							<span>{row.user_id}</span>
						</>
					) : (
						<span className="text-muted">System</span>
					)}
				</div>
			),
		},
		{
			key: 'company_id',
			label: 'Company',
			width: '100px',
			render: (row) => (
				<div className="log-company">
					{row.company_id ? (
						<>
							<Building2 className="icon" size={14} />
							<span>{row.company_id}</span>
						</>
					) : (
						<span className="text-muted">-</span>
					)}
				</div>
			),
		},
		{
			key: 'ip_address',
			label: 'IP Address',
			width: '130px',
			render: (row) => <span className="log-ip">{row.ip_address || '-'}</span>,
		},
	]

	return (
		<div className="audit-logs-page">
			<PageHeader
				title="Audit Logs"
				description="Complete activity log for security, compliance, and debugging"
				breadcrumbs={[{ label: 'Settings' }, { label: 'Audit Logs' }]}
				actions={
					<>
						<Button
							variant="outline"
							icon={<RefreshCw size={16} />}
							onClick={refresh}
							disabled={loading}
						>
							Refresh
						</Button>
						<Button
							variant="outline"
							icon={<Filter size={16} />}
							onClick={() => setShowFilters(!showFilters)}
						>
							Filters {activeFiltersCount() > 0 && `(${activeFiltersCount()})`}
						</Button>
						<Button
							variant="primary"
							icon={<Download size={16} />}
							onClick={handleExport}
							disabled={logs.length === 0}
						>
							Export CSV
						</Button>
					</>
				}
			/>

			{/* Filters Panel */}
			{showFilters && (
				<div className="filters-panel">
					<div className="filters-header">
						<h3>
							<Filter size={18} />
							Advanced Filters
						</h3>
						<Button
							variant="text"
							icon={<X size={16} />}
							onClick={() => setShowFilters(false)}
							size="small"
						/>
					</div>

					<div className="filters-grid">
						{/* User ID Filter */}
						<div className="filter-item">
							<Input
								type="number"
								label="User ID"
								placeholder="Filter by user ID"
								value={filters.user_id}
								onChange={(e) => setUserFilter(e.target.value)}
							/>
						</div>

						{/* Company ID Filter */}
						<div className="filter-item">
							<Input
								type="number"
								label="Company ID"
								placeholder="Filter by company ID"
								value={filters.company_id}
								onChange={(e) => setCompanyFilter(e.target.value)}
							/>
						</div>

						{/* Event Code Filter */}
						<div className="filter-item">
							<Select
								label="Event Code"
								value={filters.action}
								onChange={(e) => setActionFilter(e.target.value)}
								options={[
									{ value: '', label: 'All Events' },
									...distinctActions.map((action) => ({
										value: action,
										label: action,
									})),
								]}
							/>
						</div>

						{/* Date From */}
						<div className="filter-item">
							<Input
								type="date"
								label="Date From"
								value={localDateFrom}
								onChange={(e) => setLocalDateFrom(e.target.value)}
							/>
						</div>

						{/* Date To */}
						<div className="filter-item">
							<Input
								type="date"
								label="Date To"
								value={localDateTo}
								onChange={(e) => setLocalDateTo(e.target.value)}
							/>
						</div>

						{/* Filter Actions */}
						<div className="filter-item filter-actions">
							<Button
								variant="outline"
								size="small"
								onClick={handleApplyDateRange}
								disabled={!localDateFrom && !localDateTo}
							>
								Apply Date Range
							</Button>
							<Button
								variant="outline"
								size="small"
								onClick={handleClearDateRange}
								disabled={!localDateFrom && !localDateTo}
							>
								Clear Dates
							</Button>
							<Button variant="outline" size="small" onClick={handleClearAllFilters}>
								Clear All Filters
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Search Bar */}
			<div className="content-header">
				<SearchBar
					placeholder="Search in events and descriptions..."
					value={filters.search}
					onChange={setSearch}
				/>

				<div className="per-page-selector">
					<label>Show:</label>
					<select
						value={pagination.per_page}
						onChange={(e) => setPerPage(Number(e.target.value))}
						className="per-page-select"
					>
						<option value="25">25</option>
						<option value="50">50</option>
						<option value="100">100</option>
					</select>
					<span>per page</span>
				</div>
			</div>

			{/* Active Filters Display */}
			{activeFiltersCount() > 0 && (
				<div className="active-filters">
					<span className="filter-label">Active Filters:</span>
					{filters.user_id && (
						<span className="filter-tag">
							User ID: {filters.user_id}
							<X size={14} onClick={() => setUserFilter('')} />
						</span>
					)}
					{filters.company_id && (
						<span className="filter-tag">
							Company ID: {filters.company_id}
							<X size={14} onClick={() => setCompanyFilter('')} />
						</span>
					)}
					{filters.action && (
						<span className="filter-tag">
							Event: {filters.action}
							<X size={14} onClick={() => setActionFilter('')} />
						</span>
					)}
					{(filters.date_from || filters.date_to) && (
						<span className="filter-tag">
							Date Range: {filters.date_from || '...'} to {filters.date_to || '...'}
							<X size={14} onClick={handleClearDateRange} />
						</span>
					)}
				</div>
			)}

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={logs}
				loading={loading}
				emptyMessage="No audit logs found"
			/>

			{/* Pagination */}
			{pagination.total > 0 && (
				<div className="pagination">
					<div className="pagination-info">
						Showing {(pagination.page - 1) * pagination.per_page + 1} to{' '}
						{Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
						{pagination.total} logs
					</div>
					<div className="pagination-controls">
						<Button
							variant="outline"
							size="small"
							onClick={prevPage}
							disabled={pagination.page === 1 || loading}
						>
							Previous
						</Button>
						<span className="page-indicator">
							Page {pagination.page} of {pagination.total_pages}
						</span>
						<Button
							variant="outline"
							size="small"
							onClick={nextPage}
							disabled={pagination.page === pagination.total_pages || loading}
						>
							Next
						</Button>
					</div>
				</div>
			)}

			{/* Read-Only Notice */}
			<div className="read-only-notice">
				<Shield size={16} />
				<span>
					Audit logs are read-only and immutable for compliance. Logs are retained for 12 months.
				</span>
			</div>
		</div>
	)
}

export default AuditLogs
